import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from '../services/api';
import './ProfileModal.css';

interface ProfileModalProps {
  user: User | null;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ user }) => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Prüfen, ob wichtige Profilfelder fehlen
  const isProfileIncomplete = (user: User | null): boolean => {
    if (!user) return false;
    
    return (
      !user.jobtitle || 
      user.jobtitle === "" || 
      !user.profile || 
      user.profile === "" || 
      !user.picture || 
      user.picture === "" || 
      !user.linkedin || 
      user.linkedin === "" ||
      !user.gender || 
      user.gender === ""
    );
  };

  // Prüfen, ob das Modal kürzlich abgelehnt wurde
  const wasRecentlyDismissed = (): boolean => {
    const dismissedAt = localStorage.getItem("profileModalDismissedAt");
    if (!dismissedAt) return false;
    
    const dismissedTime = parseInt(dismissedAt, 10);
    const now = Date.now();
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7 Tage
    
    return (now - dismissedTime) < sevenDaysInMs;
  };

  const handleLater = useCallback(() => {
    console.log("Later button clicked!");
    
    // Speichern, wann das Modal abgelehnt wurde (für 7 Tage)
    localStorage.setItem("profileModalDismissedAt", Date.now().toString());
    console.log("Profile modal dismissed for 7 days");
    
    // Modal schließen
    setShow(false);
  }, []);

  const handleUpdateProfile = () => {
    // Modal schließen und localStorage entfernen (wird nicht mehr benötigt, wenn Profil aktualisiert wird)
    localStorage.removeItem("profileModalDismissedAt");
    setShow(false);
    
    // Zu Profil-Seite navigieren mit Back-Parameter
    navigate(`/profile?back=${encodeURIComponent(location.pathname)}`);
  };

  // ESC-Key Handler
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && show) {
        handleLater();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = '';
    };
  }, [show, handleLater]);

  useEffect(() => {
    // Prüfen, ob die aktuelle Seite "/profile" oder "/lunch-roulette" ist
    if (location.pathname === "/profile" || location.pathname.startsWith("/lunch-roulette")) {
      console.log("User is on /profile or /lunch-roulette. Modal will not be shown.");
      return;
    }

    // Prüfen, ob das Modal kürzlich abgelehnt wurde (innerhalb der letzten 7 Tage)
    if (wasRecentlyDismissed()) {
      console.log("Modal was recently dismissed (within 7 days), will not be shown.");
      return;
    }

    // Modal verzögert anzeigen (5 Sekunden Verzögerung), wenn Profil unvollständig ist
    if (user && isProfileIncomplete(user)) {
      console.log("Profile incomplete, showing modal in 5 seconds...");
      const timer = setTimeout(() => {
        setShow(true);
      }, 5000); // 5 Sekunden Verzögerung

      // Cleanup: Timer abbrechen, wenn Component unmounted wird oder sich Dependencies ändern
      return () => clearTimeout(timer);
    }
  }, [user, location.pathname]);

  const getMissingFields = (): string[] => {
    if (!user) return [];
    
    const missingFields: string[] = [];
    
    if (!user.jobtitle || user.jobtitle === "") {
      missingFields.push("Job Title");
    }
    if (!user.profile || user.profile === "") {
      missingFields.push("Profile Description");
    }
    if (!user.picture || user.picture === "") {
      missingFields.push("Profile Picture");
    }
    if (!user.linkedin || user.linkedin === "") {
      missingFields.push("LinkedIn Profile");
    }
    if (!user.gender || user.gender === "") {
      missingFields.push("Gender");
    }
    
    return missingFields;
  };

  if (!user || !isProfileIncomplete(user)) {
    return null;
  }

  const missingFields = getMissingFields();

  return (
    <>
      {/* Bootstrap Modal */}
      <div 
        className={`modal fade ${show ? 'show' : ''}`} 
        id="profileModal" 
        tabIndex={-1} 
        aria-labelledby="profileModalLabel" 
        aria-hidden={!show}
        style={{ display: show ? 'block' : 'none' }}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="profileModalLabel">
                Profile Update Required
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                aria-label="Close"
                onClick={handleLater}
              ></button>
            </div>
            <div className="modal-body">
              <p>Your profile is incomplete. Please update the following fields to get the best experience:</p>
              <ul className="list-unstyled">
                {missingFields.map((field, index) => (
                  <li key={index} className="profile-modal-missing-field mb-1">
                    <i className="bi bi-exclamation-circle text-warning"></i>
                    {field}
                  </li>
                ))}
              </ul>
              <div className="mt-3 p-3 bg-light rounded">
                <small className="text-muted">
                  <strong>Important:</strong> LinkedIn profile and profile picture are especially important for networking.
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-outline-primary" 
                onClick={handleLater}
              >
                Later
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleUpdateProfile}
              >
                Update my Profile
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal Backdrop */}
      {show && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default ProfileModal;
